import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectVehicleReport,
  selectVehicleReportStatus,
  selectVehicleMarketEstimationStatus,
  pullVehicleMarketEstimateAsync,
} from "../../../features/reports/reportsSlice";
import {
  Container,
  Padding,
  Text,
  Layout,
  Input,
  Button,
  Margin,
  Form,
} from "@kidslivesafe/sc-react";
import { formatNumberCommas } from "../../../utils";

import "./vehicleValuation.css";

import { VehicleReportType } from "../../../features/reports/reportTypes";

import { sendMemberEventAsync } from "../../../features/admin/adminSlice";

import valuationChart from "../../../assets/global/images/vehicle-valuation-chart.png";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";
import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";

const VehicleValuation: React.FC<{
  containerId: string;
  printReady?: boolean;
}> = ({ containerId, printReady }) => {
  const dispatch = useAppDispatch();
  let pulledReport = useAppSelector(selectVehicleReport) as VehicleReportType;
  const vehicleReportStatus = useAppSelector(selectVehicleReportStatus);
  const vehicleMarketEstimationStatus = useAppSelector(
    selectVehicleMarketEstimationStatus,
  );

  const [vehicleMilesInput, setVehicleMilesInput] = useState<number>(0);
  const [previousMilesSearch, setPreviousMilesSearch] = useState<number>(0);
  const [vehicleMilesError, setVehicleMilesError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [mileageUpdateCount, setMileageUpdateCount] = useState<number>(0);

  //New & Used Toggle
  const [isUsed, setIsUsed] = useState<boolean>(false);

  const currentBreakpoint = useCurrentBreakpoint();
  const loading = vehicleReportStatus === "loading";
  const vehicleMarketEstLoading = vehicleMarketEstimationStatus === "loading";

  useEffect(() => {
    if (pulledReport && pulledReport.vehicleValuation) {
      if (pulledReport.vehicleValuation.specs.miles > 8000) {
        setVehicleMilesInput(pulledReport.vehicleValuation.specs.miles);
      } else {
        setVehicleMilesInput(8000);
      }

      const modelYear = Number(pulledReport.vehicleValuation?.specs.year);
      if (!isNaN(modelYear) && modelYear < new Date().getFullYear()) {
        setIsUsed(true);
      } else {
        setIsUsed(false);
      }
    }
  }, [pulledReport]);

  const handleNewMileageSearch = () => {
    if (isNaN(vehicleMilesInput)) {
      return;
    }
    if (
      vehicleMilesInput === previousMilesSearch ||
      vehicleMilesInput === pulledReport.vehicleValuation.specs.miles
    ) {
      setVehicleMilesError(true);
      setErrorMessage("Change mileage to update the value");
      return;
    }
    if (mileageUpdateCount < 5) {
      if (vehicleMilesInput > 500000) {
        setVehicleMilesError(true);
        setErrorMessage("Mileage exceeds 500,000 limit");
      } else if (vehicleMilesInput < 8000) {
        setVehicleMilesError(true);
        setErrorMessage("Mileage is below 8,000 minimum");
      } else {
        setMileageUpdateCount(prev => prev + 1);
        setPreviousMilesSearch(vehicleMilesInput);
        sendMemberEvent();
        handlePullVehicleMarketEstimate();
      }
    } else {
      setVehicleMilesError(true);
      sendMemberEvent();
      setErrorMessage("Search count limit reached. Try again later.");
    }
  };

  const sendMemberEvent = () => {
    dispatch(
      sendMemberEventAsync({
        EventCategory: "vehicle_value",
        EventAction: "Click",
        MetaData: JSON.stringify({
          key: "marketEstimate",
          value: `Mileage Update Count: ${mileageUpdateCount}`,
        }),
      }),
    );
  };

  const handlePullVehicleMarketEstimate = async () => {
    await dispatch(
      pullVehicleMarketEstimateAsync({
        vin: pulledReport.vehicleSummary.vin,
        miles: vehicleMilesInput,
      }),
    );
  };
  return (
    <>
      <Padding
        space="none"
        breakpointStyles={{
          mobile: {
            left: "md",
            right: "md",
          },
        }}
      >
        <Container
          containerId={containerId}
          containerTitle={`Estimated Value ${
            loading
              ? ""
              : `(${
                  pulledReport && pulledReport.vehicleValuation
                    ? (pulledReport.vehicleValuation.predictedPrice != null
                        ? 1
                        : 0) +
                      (pulledReport.vehicleValuation.priceRange?.lowerBound !=
                      null
                        ? 1
                        : 0) +
                      (pulledReport.vehicleValuation.priceRange?.upperBound !=
                      null
                        ? 1
                        : 0)
                    : 0
                })`
          }`}
          tooltipContent="This is a general estimate and may not reflect the car’s exact condition. Mileage is based on the last recorded reading, but you can update it if it’s inaccurate. Click ‘Update’ to see how the value is adjusted based on your changes."
        >
          {loading ? (
            <LoadingContainerLineItem
              lineItemHeader
              lineItemSubHeader
              lineItemSubHeaderLine2
              lineItemDetails
              numberToLoad={5}
            />
          ) : pulledReport && pulledReport.vehicleValuation ? (
            <>
              <Padding space="md">
                <Layout
                  type="flex"
                  flexProps={{ direction: "column", alignItems: "center" }}
                >
                  <Text size="base" fontWeight="semibold" textAlign="center">
                    Estimated {isUsed ? "Used" : "New"} Retail Value
                  </Text>
                  <Text size="xl" fontWeight="bold" textAlign="center">
                    {pulledReport.vehicleValuation.predictedPrice
                      ? "$" +
                        formatNumberCommas(
                          pulledReport.vehicleValuation.predictedPrice,
                        )
                      : "UNAVAILABLE"}
                  </Text>
                  <Padding top="md" bottom="md">
                    <Text size="xs" fontWeight="normal">
                      Estimated value range:{" "}
                      {pulledReport.vehicleValuation.priceRange.lowerBound
                        ? "$" +
                          formatNumberCommas(
                            pulledReport.vehicleValuation.priceRange.lowerBound,
                          )
                        : "N/A"}{" "}
                      -{" "}
                      {pulledReport.vehicleValuation.priceRange.upperBound
                        ? "$" +
                          formatNumberCommas(
                            pulledReport.vehicleValuation.priceRange.upperBound,
                          )
                        : "N/A"}
                    </Text>
                  </Padding>
                  <Container>
                    <Layout
                      type="flex"
                      flexProps={{ direction: "column", alignItems: "center" }}
                      passThroughClassess="display-relative"
                    >
                      <img
                        src={valuationChart}
                        alt="vehicle valuation chart"
                        width={currentBreakpoint === "mobile" ? 320 : 550}
                      />

                      <div className="valuationText value-rough">
                        <Text size="xs" fontWeight="normal">
                          Rough
                        </Text>
                        <Text
                          size={currentBreakpoint === "mobile" ? "base" : "xl"}
                          fontWeight="bold"
                        >
                          {pulledReport.vehicleValuation.priceRange.lowerBound
                            ? "$" +
                              formatNumberCommas(
                                pulledReport.vehicleValuation.priceRange
                                  .lowerBound,
                              )
                            : "-"}
                        </Text>
                      </div>
                      <div className="valuationText value-ave">
                        <Text size="xs" fontWeight="normal">
                          Average
                        </Text>
                        <Text size="xxl" fontWeight="bold">
                          {pulledReport.vehicleValuation.predictedPrice
                            ? "$" +
                              formatNumberCommas(
                                pulledReport.vehicleValuation.predictedPrice,
                              )
                            : "-"}
                        </Text>
                      </div>
                      <div className="valuationText value-clean">
                        <Text size="xs" fontWeight="normal">
                          Clean
                        </Text>
                        <Text
                          size={currentBreakpoint === "mobile" ? "base" : "xl"}
                          fontWeight="bold"
                        >
                          {pulledReport.vehicleValuation.priceRange.upperBound
                            ? "$" +
                              formatNumberCommas(
                                pulledReport.vehicleValuation.priceRange
                                  .upperBound,
                              )
                            : "-"}
                        </Text>
                      </div>
                    </Layout>
                  </Container>
                  <Form>
                    <Layout
                      type="flex"
                      flexProps={{
                        direction: "row",
                        alignItems: vehicleMilesError ? "center" : "flex-end",
                      }}
                    >
                      <Input
                        id="valuation-input"
                        type="text"
                        placeholder="mileage"
                        label="Mileage"
                        fill={
                          currentBreakpoint === "mobile"
                            ? "fill"
                            : "not-fill-min-width"
                        }
                        value={Number(
                          String(vehicleMilesInput),
                        ).toLocaleString()}
                        handleChange={input => {
                          setVehicleMilesError(false);
                          const cleaned = input.replace(/,/g, "");
                          if (/^\d*$/.test(cleaned) && Number(cleaned) >= 0) {
                            setVehicleMilesInput(Number(cleaned));
                          }
                        }}
                        error={vehicleMilesError}
                        errorText={errorMessage}
                      ></Input>
                      <Margin left="sm">
                        <Button
                          buttonText="Update"
                          size="md"
                          fill="default"
                          color="primary"
                          handleClick={handleNewMileageSearch}
                          loading={vehicleMarketEstLoading}
                        />
                      </Margin>
                    </Layout>
                  </Form>
                </Layout>
              </Padding>
              <Container altColor="grey">
                <Padding space="md">
                  <Text size="xs">
                    <Text size="xs" tag="b">
                      Disclaimer:
                    </Text>
                    {"  "}
                    This estimate is based on trim, mileage, and location and
                    not the exact vehicle being researched. For an accurate
                    value based on the specific vehicle’s condition, consider a
                    professional appraisal.
                  </Text>
                </Padding>
              </Container>
            </>
          ) : (
            <Padding space="md">
              <Text size="sm" tag="b">
                No Estimate found for this vehicle
              </Text>
            </Padding>
          )}
        </Container>
      </Padding>
    </>
  );
};

export default VehicleValuation;
